import EnergyChip from "@/components/listing/EnergyChip";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/i18n/routing";
import { DEFAULT_IMAGE } from "@/lib/data/images";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import { ListingSearchData } from "@/lib/types/search";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  DoorOpen,
  Expand,
  MapPin,
  Sofa,
  X,
} from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import Image from "next/image";
import React, { useEffect, useState } from "react";

type Props = {
  activeGroup: Array<string>;
  onClose: () => void;
  index: number;
  setIndex: (index: number) => void;
  locale: string;
};

const fetchListingItem = async (
  locale: string,
  listingId: string,
): Promise<ListingSearchData | null> => {
  const params = new URLSearchParams();
  params.append("locale", locale);
  params.append("listingId", listingId);

  const response = await fetch(
    `/api/site/public/listing/search/item?${params.toString()}`,
    {
      method: "GET",
    },
  );

  const data = (await response.json()) as {
    success?: boolean;
    message?: string;
    data: ListingSearchData;
  };

  if (!response.ok) {
    console.error("Failed to fetch listing", data);
    return null;
  }

  return data.data;
};

const listingQueryOptions = (locale: string, listingId: string) => ({
  queryKey: ["map", "view", "listing-id", listingId] as const,
  staleTime: 1000 * 60 * 15,
  gcTime: 1000 * 60 * 60,
  queryFn: () => fetchListingItem(locale, listingId),
});

/**
 * Map listing spotlight: photo-first panel docked to the top-right of the map
 * on desktop (always visible where the user clicks) and as a bottom sheet on
 * mobile. Price sits on the photo, facts in a divided stat row; arrows page
 * through the clicked group one listing at a time (neighbors prefetched).
 */
const MapListingViewer: React.FC<Props> = ({
  activeGroup,
  index,
  onClose,
  setIndex,
  locale,
}) => {
  const t = useTranslations("map");
  const tListing = useTranslations("listing");
  const tUi = useTranslations("ui");
  const formatter = useFormatter();
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState(1);

  const currentId = activeGroup[index];

  const { data, isLoading } = useQuery({
    ...listingQueryOptions(locale, currentId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Prefetch the neighbors so the arrows feel instant.
  useEffect(() => {
    for (const neighbor of [index - 1, index + 1]) {
      const id = activeGroup[neighbor];
      if (id) {
        queryClient.prefetchQuery(listingQueryOptions(locale, id));
      }
    }
  }, [index, activeGroup, locale, queryClient]);

  const onMove = (step: -1 | 1) => {
    setDirection(step);
    const next = (index + step + activeGroup.length) % activeGroup.length;
    setIndex(next);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ type: "spring", stiffness: 320, damping: 30 }}
      className="absolute inset-x-2 bottom-2 z-50 sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-4 sm:w-[400px]"
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-[0_18px_44px_-18px_rgba(24,24,27,0.55)]">
        {/* photo hero with overlays */}
        <div className="relative aspect-[16/10] w-full sm:aspect-[4/3] sm:max-h-[300px]">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={currentId}
              initial={{ opacity: 0, x: 64 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -64 * direction }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="absolute inset-0"
            >
              {isLoading || !data ? (
                <Skeleton className="absolute inset-0 rounded-none" />
              ) : (
                <>
                  <Image
                    src={data.imageUrl || DEFAULT_IMAGE.LISTING}
                    alt={data.title}
                    width={800}
                    height={600}
                    unoptimized
                    sizes={IMAGE_SIZES.cardMedium}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-3 left-4 flex flex-row items-end gap-2 text-white">
                    <p className="text-2xl font-bold leading-none">
                      {formatter.number(data.attributes.price, {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 0,
                      })}
                    </p>
                    <p className="text-xs font-medium text-white/85">
                      {tListing("price-month")}
                    </p>
                    {data.attributes.energyLabel && (
                      <EnergyChip
                        label={data.attributes.energyLabel}
                        className="ml-1"
                      />
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>

          <span className="absolute left-3 top-3 rounded-full bg-foreground/80 px-3 py-1 text-[11px] font-semibold text-background backdrop-blur">
            {t("listings-here", { count: activeGroup.length })}
          </span>
          <button
            aria-label={tUi("close")}
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-full bg-background/95 text-foreground shadow-sm transition-colors hover:bg-background"
          >
            <X className="size-4" />
          </button>

          {activeGroup.length > 1 && (
            <>
              <button
                aria-label={t("previous-listing")}
                type="button"
                onClick={() => onMove(-1)}
                className="absolute left-3 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md transition-transform hover:scale-105"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                aria-label={t("next-listing")}
                type="button"
                onClick={() => onMove(1)}
                className="absolute right-3 top-1/2 inline-flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md transition-transform hover:scale-105"
              >
                <ChevronRight className="size-5" />
              </button>
              <span className="absolute bottom-3 right-3 rounded-full bg-background/90 px-2.5 py-0.5 text-[11px] font-bold text-foreground">
                {index + 1} / {activeGroup.length}
              </span>
            </>
          )}
        </div>

        {/* details */}
        <div className="flex flex-col gap-2.5 px-4 pb-4 pt-3">
          {isLoading || !data ? (
            <>
              <Skeleton className="h-5 w-4/5" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-11 w-full rounded-xl" />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-0.5">
                <Link
                  href={data.href}
                  title={data.title}
                  className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground hover:underline"
                >
                  {data.title}
                </Link>
                <p className="flex flex-row items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="size-3.5 min-w-3.5" />
                  {data.location.postalCode
                    ? `${data.location.postalCode} · ${data.location.city}`
                    : data.location.city}
                </p>
              </div>

              <div className="grid grid-cols-3 divide-x divide-border rounded-xl border border-border">
                <StatCell
                  icon={<Expand className="size-4" strokeWidth={1.5} />}
                  value={tListing("size", { size: data.attributes.size })}
                />
                <StatCell
                  icon={<DoorOpen className="size-4" strokeWidth={1.5} />}
                  value={tListing("rooms", {
                    count: data.attributes.roomsCount,
                  })}
                />
                <StatCell
                  icon={<Sofa className="size-4" strokeWidth={1.5} />}
                  value={tListing(`furnished.${data.attributes.furnishedType}`)}
                />
              </div>

              <Button
                asChild
                variant="secondary"
                className="h-11 w-full font-semibold"
              >
                <Link href={data.href} title={data.title}>
                  <span>{tListing("see-details")}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MapListingViewer;

type StatCellProps = {
  icon: React.ReactNode;
  value: string;
};

const StatCell: React.FC<StatCellProps> = ({ icon, value }) => {
  return (
    <div className="flex flex-col items-center gap-1 px-1 py-2.5">
      <span className="text-muted-foreground">{icon}</span>
      <span className="max-w-full truncate text-xs font-semibold text-foreground">
        {value}
      </span>
    </div>
  );
};
